import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusableElements = (container) => Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || [])
  .filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');

function useModalFocusTrap({
  active,
  containerRef,
  triggerRef,
  onClose,
  backgroundSelectors = []
}) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;

    const container = containerRef.current;
    const backgroundElements = backgroundSelectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)))
      .filter((element, index, all) => !container.contains(element) && all.indexOf(element) === index);
    const previousBackgroundState = backgroundElements.map((element) => ({
      element,
      ariaHidden: element.getAttribute('aria-hidden'),
      inert: element.inert
    }));

    backgroundElements.forEach((element) => {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    });

    const initialFocusTarget = getFocusableElements(container)[0] || container;
    initialFocusTarget.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousBackgroundState.forEach(({ element, ariaHidden, inert }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      triggerRef.current?.focus();
    };
  }, [active, backgroundSelectors, containerRef, onClose, triggerRef]);
}

export default useModalFocusTrap;
